'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';

import { Link } from '@natu/next-link';
import { getEventPayload } from '@natu/utils/getEventPayload';
import { usePageContext } from '@natu/utils/PageContextProvider';

import {
  getAssetFromStoryblok,
  getLinkPropsFromStoryblok,
  sbEditable,
  type SBProps,
  type StoryblokAsset,
  type StoryblokLink,
} from '../../../utils';

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

const Button = dynamic(() => import('@natu/ui/Button').then(mod => mod.Button));

const DynamicIcon = dynamic(() => import('@natu/ui/DynamicIcon').then(mod => mod.DynamicIcon));

interface SBTechnologyCardProps {
  title?: string;
  description?: string;
  link?: StoryblokLink;
  linkText?: string;
  icon?: StoryblokAsset;
}

export const SBTechnologyCard = ({ blok }: SBProps<SBTechnologyCardProps>) => {
  const pathname = usePathname();
  const { isCampaign } = usePageContext() || {};

  const { title, description, link, icon, linkText } = blok;

  const image = getAssetFromStoryblok(icon, { type: 'image' });
  const linkProps = getLinkPropsFromStoryblok(link);

  const event = 'E_cta_to_landing';
  const eventPayload = getEventPayload({ pathname, event, isCampaign });

  const cardBody = (
    <div>
      <div className="mb-2 brightness-0 invert filter-none transition-all group-hover:filter md:mb-6">
        {image?.src && <ResponsiveImage {...image} />}
      </div>
      <h3 className="mb-4 text-xl font-semibold md:text-2xl">{title}</h3>
      <p className="text-gray-600 transition-colors group-hover:text-white">{description}</p>
    </div>
  );

  const wrapperClassName =
    'flex flex-col justify-between h-full gap-y-8 border group p-6 md:p-8 rounded-xl bg-white hover:bg-primary-600 hover:text-white hover:shadow-2xl transition-colors border border-transparent hover:border-gray-200 group';

  if (!linkProps || linkProps.href === '')
    return (
      <article className={wrapperClassName} {...sbEditable(blok)}>
        {cardBody}
      </article>
    );

  return (
    <Link
      className={wrapperClassName}
      onClick={() => sendGTMEvent({ event, ...eventPayload })}
      {...sbEditable(blok)}
      {...linkProps}
    >
      {cardBody}
      {linkText && (
        <div className="mt-auto brightness-0 invert filter-none transition-all group-hover:filter">
          <Button
            variant="link"
            size="link"
            title={linkText}
            className="h-6 text-sm font-semibold group-hover:decoration-white"
            asChild
          >
            <div>
              {linkText}
              <DynamicIcon className="ml-2 h-5 w-5" icon="arrowRight" />
            </div>
          </Button>
        </div>
      )}
    </Link>
  );
};
